import type { TFunction } from 'i18next';
import type { GeneratedForm } from '../../../lib/flotiq-client/types/external-api-response';

import { getEnv } from '../../../lib/env';
import { postContentObject } from '../../../lib/flotiq-client';
import {
  defaultResponseParser,
  fetchMethod,
} from '../../../lib/flotiq-client/base-request';
import { generateForm as generateFromAPICall } from '../../../lib/flotiq-client/external-services-requests';
import { parseResponseErrors } from '../../../lib/flotiq-client/response-errors';
import { removeTrailingSlash } from '../../../lib/helpers';

import { ErrorType, PopulateSpaceError } from './PopulateSpaceError';

export const generateForms = async (
  userRwApiKey: string,
  applicationRoApiKey: string,
  userId: string,
  t: TFunction,
  jwt?: string,
) => {
  try {
    const generateForms = await getGeneratedForms(applicationRoApiKey);
    if (generateForms.length === 0) {
      return;
    }

    const errors: Record<string, string>[] = [];
    await Promise.all(
      generateForms.map(async (form) => {
        const success = await generateForm(form, userRwApiKey, userId, jwt);
        if (!success) {
          errors.push({
            ctd: form.CTDslug,
            error: t('PopulateSpaceModal.Error.FormGenerationFailed'),
          });
        }
      }),
    );

    if (errors.length) {
      throw new Error(
        t('PopulateSpaceModal.Error.FormGenerationErrorMessage', {
          message: errors.map(({ ctd }) => ctd).join(', '),
        }),
      );
    }
  } catch (e) {
    if (e instanceof Error) {
      throw new PopulateSpaceError(e.message, ErrorType.GENERATED_FORMS);
    }
  }
};

const generateForm = async (
  flotiqForm: GeneratedForm,
  userRwApiKey: string,
  userId: string,
  jwt?: string,
) => {
  try {
    const {
      body: formBody,
      status: formStatus,
      ok: formOk,
    } = await generateFromAPICall(jwt, {
      origin: flotiqForm.origin,
      flotiqApiKey: userRwApiKey,
      config: JSON.parse(flotiqForm.config),
      messages: JSON.parse(flotiqForm.messages),
      CTDslug: flotiqForm.CTDslug,
      userIdentifier: userId,
    });

    if (!formOk) {
      throw parseResponseErrors(formBody, formStatus);
    }

    const { body, status, ok } = await postContentObject(jwt, undefined, {
      contentTypeName: '_generated_forms',
      ...flotiqForm,
      id: `_generated_forms-${userId}-${flotiqForm.CTDslug}`,
      formUrl: formBody.formUrl,
      flotiqApiKey: userRwApiKey,
      userIdentifier: userId,
    });
    if (!ok) {
      throw parseResponseErrors(body, status);
    }
    return true;
  } catch {
    return false;
  }
};

const getGeneratedForms = async (applicationRoApiKey: string) => {
  const { body, status, ok } = await fetchMethod(
    removeTrailingSlash(
      getEnv('VITE_FLOTIQ_ONBOARDING_APPS_CTD_URL').split(
        '/api/v1/content/',
      )[0],
    ) + '/api/v1/content/_generated_forms?limit=1000&page=1',
    'GET',
    undefined,
    undefined,
    {
      headers: {
        'X-Auth-Token': applicationRoApiKey,
      },
    },
  ).then(defaultResponseParser);

  if (!ok) {
    throw parseResponseErrors(body, status);
  }

  return body.data as unknown as GeneratedForm[];
};

export default generateForms;
